import { z } from 'zod';

/**
 * A sponsor reference that is structurally safe to show outside the
 * access-controlled datastore: an internaltools company number of at most five
 * digits, provider-qualified.
 *
 * The client ref a member submits with an eligibility check is free text (the
 * request accepts any string up to 120 characters), so it may carry a name, an
 * email or a date. Only a short company number survives this shape; a number of
 * five digits cannot carry a name, an email or a date of birth (ADR-069).
 *
 * Dependency-free on purpose: `client-ref.ts` imports from `schema.ts`, so
 * `schema.ts` reading this module through `client-ref.ts` would be an import
 * cycle.
 */
export const SAFE_SPONSOR_REF_PATTERN = /^internaltools:[1-9][0-9]{0,4}$/;

export const safeSponsorRefSchema = z.string().regex(SAFE_SPONSOR_REF_PATTERN);

const COMPANY_NUMBER = /^[1-9][0-9]{0,4}$/;
const QUALIFIED_PREFIX = 'internaltools:';

/**
 * `internaltools:<n>` when `raw` is `<n>` or `internaltools:<n>` with `<n>` a
 * company number of one to five digits and no leading zero; `null` for
 * anything else, including a slug, a name, an email, a date or another
 * provider's ref.
 */
export function toSafeSponsorRef(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const bare = raw.startsWith(QUALIFIED_PREFIX) ? raw.slice(QUALIFIED_PREFIX.length) : raw;
  return COMPANY_NUMBER.test(bare) ? `${QUALIFIED_PREFIX}${bare}` : null;
}
